import { CalculateConfigurationCoordinatingGroupParticipantSourceDataAllocations, ConfigurationCoordinatingGroup as ConfigurationCoordinatingGroupDto, ConfigurationStatus, SaveConfigurationCoordinatingGroup, SourceDataAllocationType } from "@/api/dtos";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Text from "@/components/forms/inputs/Text";
import SetLinks from "@/components/layouts/SetLinks";
import useConfigurationCoordinatingGroup from "@/hooks/data/useConfigurationCoordinatingGroup";
import { FC, useCallback, useContext, useMemo } from "react";

import UneditableText from "@/components/forms/inputs/UneditableText";
import ConfigurationContextTemplate, { IConfigurationContextTemplateProps } from "@/components/forms/templates/ConfigurationContextTemplate";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationCoordinatingGroupsTrialCreateLink } from "@/contexts/layouts/LinkContext";
import { useLocation } from "react-router-dom";


const allocationTypes = [
    SourceDataAllocationType.Constant,
    SourceDataAllocationType.FirstXParticipants,
    SourceDataAllocationType.RepeatingCycle,
    SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle,
    SourceDataAllocationType.Inherit
];

const statuses = [
    ConfigurationStatus.Disabled,
    ConfigurationStatus.ReadOnly,
    ConfigurationStatus.Enabled
];


const ConfigurationCoordinatingGroup: FC = () => {
    const { data: trial, oid: trialOid } = useContext(TrialContext);
    const { data: coordinatingGroup } = useContext(CoordinatingGroupContext);

    const contextQuery = useMemo(() => {
        if (!coordinatingGroup?.id) {
            return undefined;
        }

        return {
            trialId: coordinatingGroup.trialId,
            masterGroupId: coordinatingGroup.masterGroupId,
            coordinatingGroupId: coordinatingGroup.id
        }
    }, [coordinatingGroup]);

    const { data: configurationCoordinatingGroup, loading: configurationCoordinatingGroupLoading, validating: configurationCoordinatingGroupValidating } = useConfigurationCoordinatingGroup(contextQuery, { revalidateOnFocus: false });

    const loading = configurationCoordinatingGroupLoading;
    const validating = configurationCoordinatingGroupValidating;

    const location = useLocation();

    const returnUrl = useMemo(() => {
        if (location.state?.from) {
            return `${location.state.from.pathname}${location.state.from.search}`
        }
        
        return `/${trialOid}/configuration/coordinating-groups`;
    }, [trialOid, location])

    const onSave = useCallback<IConfigurationContextTemplateProps<ConfigurationCoordinatingGroupDto>['onSave']>(async (client, data, allocations, updates) => {
        await client.post(new SaveConfigurationCoordinatingGroup({ data: data, allocations: allocations, participantSourceDataAllocationUpdates: updates}));
    }, []);

    const onCalculateAllocations = useCallback<IConfigurationContextTemplateProps<ConfigurationCoordinatingGroupDto>['onCalculateAllocations']>(async (client, contextQuery, data, allocations) => {
        const response = await client.post(new CalculateConfigurationCoordinatingGroupParticipantSourceDataAllocations({ 
            ...contextQuery,
            configurationCoordinatingGroup: data, 
            allocations: allocations
        }));

        return response;
    }, []);
    
    const onValidate = useCallback<IConfigurationContextTemplateProps<ConfigurationCoordinatingGroupDto>['onValidate']>(async (client, errors, formState) => {

        return errors;
    }, []);

    return (
        <>
            <SetLinks 
                createTrialLink={ConfigurationCoordinatingGroupsTrialCreateLink} 
                institutionLinkEnabled={false} 
                participantLinkEnabled={false} 
            />
            <ConfigurationContextTemplate
                allocationTypes={allocationTypes}
                entityName="Coordinating Group"
                title={coordinatingGroup?.name}
                onCalculateAllocations={onCalculateAllocations}
                onSave={onSave}
                onValidate={onValidate}
                statuses={statuses}
                configuration={configurationCoordinatingGroup}
                configurationLoading={loading}
                configurationValidating={validating}
                contextQuery={contextQuery}
                returnUrl={returnUrl}
                children={
                    <>
                        <InputOnlyField
                            propertyPath="originalName"
                            label="Default Name"
                            helperText="The Default Name of the coordinating group."
                            margin="normal"
                            component={UneditableText}
                            disabled
                            value={coordinatingGroup?.defaultName}
                        />
                        <InputOnlyField
                            propertyPath="configuration.name"
                            label="Name"
                            helperText="Enter a name to override the Default Name of the coordinating group."
                            margin="normal"
                            component={Text}
                        />
                    </>
                }
            />
        </>
    )
};

export default ConfigurationCoordinatingGroup;